import { useState, useEffect } from "react";
import api from "../services/api";
import { uploadFlavorImageToS3 } from "../utils/uploadToS3";

const useFlavorFormData = (flavor, onClose, onSave) => {
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    icing: "",
    price: "",
    popularity: 0,
    isPremium: false,
    basePriceIncluded: true,
    dietaryTags: [],
    category: [],
    allergens: [],
    ingredients: [],
    seo: { metaTitle: "", metaDescription: "" },
    images: { primary: "", secondary: [] },
    visibility: "public",
  });

  const [primaryImageFile, setPrimaryImageFile] = useState(null);
  const [secondaryImageFiles, setSecondaryImageFiles] = useState([]);
  const [primaryPreview, setPrimaryPreview] = useState("");
  const [secondaryPreviews, setSecondaryPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [icings, setIcings] = useState([]);
  const [allergenOptions, setAllergenOptions] = useState([]);
  const [dietaryTagsOptions, setDietaryTagsOptions] = useState([]);
  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);

  useEffect(() => {
    fetchOptions();
    fetchIngredients();

    if (flavor) {
      setFormData({
        ...flavor,
        isPremium: flavor.isPremium ?? false,
        basePriceIncluded: flavor.basePriceIncluded ?? true,
        category: flavor.category || [],
        allergens: flavor.allergens || [],
        dietaryTags: flavor.dietaryTags || [],
        ingredients: flavor.ingredients || [],
        images: {
          primary: flavor.images?.primary || "",
          secondary: flavor.images?.secondary || [],
        },
        visibility: flavor.visibility || "public",
      });

      setPrimaryPreview(flavor.images?.primary || "");
      setSecondaryPreviews(flavor.images?.secondary || []);
    }
  }, [flavor]);

  const fetchOptions = async () => {
    try {
      const [icingRes, allergensRes, dietaryRes, ingredientsRes] =
        await Promise.all([
          api.get("/icings"),
          api.get("/admin/allergens"),
          api.get("/admin/dietary-tags"),
          api.get("/admin/ingredients"),
        ]);

      setIcings(icingRes.data);
      setAllergenOptions(allergensRes.data);
      setDietaryTagsOptions(dietaryRes.data);
      setIngredientOptions(ingredientsRes.data);
    } catch (err) {
      console.error("Error loading flavor form options", err);
    }
  };

  const fetchIngredients = async () => {
    setLoadingIngredients(true);
    try {
      const res = await api.get("/admin/ingredients");
      setIngredientOptions(res.data);
    } catch (err) {
      console.error("Failed to fetch ingredients", err);
    } finally {
      setLoadingIngredients(false);
    }
  };

  const handlePrimaryImageChange = (e) => {
    const file = e.target.files[0];
    setPrimaryImageFile(file);
    setPrimaryPreview(URL.createObjectURL(file));
  };

  const handleSecondaryImagesChange = (e) => {
    const files = [...e.target.files];
    setSecondaryImageFiles(files);
    setSecondaryPreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);

      if (!formData.category || formData.category.length === 0) {
        alert("Please select at least one category.");
        setUploading(false);
        return;
      }

      let primaryImageUrl = formData.images.primary;
      let secondaryImageUrls = formData.images.secondary;

      // if (!primaryImageFile && !primaryImageUrl) {
      //   alert("Please upload a primary image.");
      //   setUploading(false);
      //   return;
      // }

      if (primaryImageFile) {
        const uploaded = await uploadFlavorImageToS3(
          primaryImageFile,
          formData.name,
          "primary"
        );
        primaryImageUrl = uploaded?.fileUrl;
      }

      if (secondaryImageFiles.length > 0) {
        const uploadedSecondary = await Promise.all(
          secondaryImageFiles.map((file) =>
            uploadFlavorImageToS3(file, formData.name, "secondary")
          )
        );
        secondaryImageUrls = uploadedSecondary.map((file) => file?.fileUrl);
      }

      const payload = {
        ...formData,
        price: Number(formData.price),
        images: {
          primary: primaryImageUrl,
          secondary: secondaryImageUrls,
        },
      };

      if (flavor) {
        await api.put(`/flavors/${flavor._id}`, payload);
      } else {
        await api.post("/flavors", payload);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error("Error saving flavor:", err);
    } finally {
      setUploading(false);
    }
  };

  return {
    formData,
    setFormData,
    icings,
    dietaryTagsOptions,
    allergenOptions,
    ingredientOptions,
    loadingIngredients,
    primaryPreview,
    secondaryPreviews,
    handlePrimaryImageChange,
    handleSecondaryImagesChange,
    handleSubmit,
    uploading,
  };
};

export default useFlavorFormData;
